import { useContext } from 'react'
import { useTranslation } from 'react-i18next'

import { ToastContext } from '@reveni-io/reveni-ui'

import SaveChangesButtons from '@/common/components/SaveChangesButtons/SaveChangesButtons'
import UnsavedChangesAlert from '@/common/components/UnsavedChangesAlert/UnsavedChangesAlert'
import { userPermissions } from '@/common/constants'
import AppContext from '@/common/context/app.context'
import useUserPermissions from '@/common/hooks/useUserPermissions/useUserPermissions'
import { configurationTypes } from '@/configuration/pages/ConfigurationPage/constants'
import { getInfoWithUpdatedPreferences } from '@/configuration/pages/ConfigurationPage/helpers'
import useReturnPolicyConfiguration from '@/configuration/pages/ConfigurationPage/ReturnPolicy/ReturnPolicyConfiguration/hooks/useReturnPolicyConfiguration/useReturnPolicyConfiguration'

import CustomerContactMethod from './CustomerContactMethod/CustomerContactMethod'
import ExchangeRecommendationTypeSelector from './ExchangeRecommendationTypeSelector/ExchangeRecommendationTypeSelector'
import OrderIdentification from './OrderIdentification/OrderIdentification'
import ReturnsPortalConfigurationPageSkeleton from './ReturnsPortalConfigurationPageSkeleton/ReturnsPortalConfigurationPageSkeleton'
import SkipRefundMethodStep from './SkipRefundMethodStep/SkipRefundMethodStep'
import TaxonomyFilterConfiguration from './TaxonomyFilterConfiguration/TaxonomyFilterConfiguration'
import TaxonomyTypeSelector from './TaxonomyTypeSelector/TaxonomyTypeSelector'
import { getPayload } from './helpers'
import * as Styles from './styles'

const ReturnsPortalConfigurationPage = () => {
  const { t } = useTranslation()
  const { info, updateContext } = useContext(AppContext)
  const { setToastInfo } = useContext(ToastContext)
  const provider = info?.selectedStore?.provider
  const {
    configurationPreferences,
    allowedExchangeMethods,
    productVariantExchangeRecommendationType,
    setProductVariantExchangeRecommendationType,
    productVariantExchangeRecommendationValue,
    setProductVariantExchangeRecommendationValue,
    productCategories,
    productCollections,
    loadingConfigurationPreferences,
    productVariantExchangeTaxonomyType,
    setProductVariantExchangeTaxonomyType,
    productVariantExchangeTaxonomyDisplay,
    setProductVariantExchangeTaxonomyDisplay,
    productTypes,
    productVariantExchangeTaxonomyIds,
    setProductVariantExchangeTaxonomyIds,
    hasChanges,
    entityId,
    savePreferences,
    refetchConfigurationPreferences,
    revertChanges,
    savingPreferences,
    gettingProductCategories,
    gettingProductCollections,
    fetchingProductTypes,
    customerContactMethod,
    setCustomerContactMethod,
    availableTaxonomyTypes,
    availableRecommendationTypes,
    allowsOrderRecovery,
    setAllowsOrderRecovery,
    hasOrderRecoveryEmailTemplate,
    skipSingleRefundMethodStep,
    setSkipSingleRefundMethodStep,
  } = useReturnPolicyConfiguration({
    storeId: info?.selectedStore?.id,
    merchantId: info?.id,
    returnPortalId: info?.selectedReturnPortal?.id,
    configurationMode: configurationTypes.returnsPortal,
  })
  const allowedPreferences = Object.keys(configurationPreferences || {})
  const externalConnectedGateway = info?.billing_data?.external_connected_gateway
  const { hasPermission } = useUserPermissions()

  const saveSettingsAndUpdateContext = async preferencesPayload => {
    updateContext(info => getInfoWithUpdatedPreferences(info, preferencesPayload))

    await refetchConfigurationPreferences(entityId)
  }

  const handleSavePreferences = async payload => {
    if (hasChanges) {
      await savePreferences({ ...entityId, payload: { ...configurationPreferences, ...payload } })
    }

    await saveSettingsAndUpdateContext(payload)
  }

  const onSavePreferences = async () => {
    const payload = getPayload({
      productVariantExchangeRecommendationType,
      productVariantExchangeRecommendationValue,
      productVariantExchangeTaxonomyType,
      productVariantExchangeTaxonomyDisplay,
      productVariantExchangeTaxonomyIds,
      customerContactMethod,
      allowsOrderRecovery,
      skipSingleRefundMethodStep,
    })

    try {
      await handleSavePreferences(payload)
      setToastInfo({ show: true, message: t('Guardado correctamente'), type: 'success' })
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error)
      setToastInfo({ show: true, message: error.errorMessage, type: 'error' })
    }
  }

  const handleResetChanges = () => {
    revertChanges()
  }

  if (
    loadingConfigurationPreferences ||
    gettingProductCategories ||
    gettingProductCollections ||
    fetchingProductTypes
  ) {
    return <ReturnsPortalConfigurationPageSkeleton />
  }

  return (
    <Styles.Container>
      {hasChanges && <UnsavedChangesAlert hasPermission={hasPermission(userPermissions.editPolicy)} />}
      <CustomerContactMethod
        customerContactMethod={customerContactMethod}
        onChangeCustomerContactMethod={setCustomerContactMethod}
      />
      <div>
        <TaxonomyTypeSelector
          allowedExchangeMethods={allowedExchangeMethods}
          allowedPreferences={allowedPreferences}
          provider={provider}
          configurationPreferences={configurationPreferences}
          externalConnectedGateway={externalConnectedGateway}
          taxonomyType={productVariantExchangeTaxonomyType}
          onChangeTaxonomyType={setProductVariantExchangeTaxonomyType}
          availableTaxonomyTypes={availableTaxonomyTypes}
        />
        <TaxonomyFilterConfiguration
          taxonomyType={productVariantExchangeTaxonomyType}
          allowedExchangeMethods={allowedExchangeMethods}
          allowedPreferences={allowedPreferences}
          provider={provider}
          configurationPreferences={configurationPreferences}
          externalConnectedGateway={externalConnectedGateway}
          taxonomyDisplay={productVariantExchangeTaxonomyDisplay}
          onChangeTaxonomyDisplay={setProductVariantExchangeTaxonomyDisplay}
          productCategories={productCategories}
          productCollections={productCollections}
          productTypes={productTypes}
          taxonomyIds={productVariantExchangeTaxonomyIds}
          setTaxonomyIds={setProductVariantExchangeTaxonomyIds}
        />
      </div>
      <ExchangeRecommendationTypeSelector
        typeValue={productVariantExchangeRecommendationType}
        onChangeType={setProductVariantExchangeRecommendationType}
        onChangeValue={setProductVariantExchangeRecommendationValue}
        categories={productCategories}
        collections={productCollections}
        recommendationValue={productVariantExchangeRecommendationValue}
        provider={provider}
        allowedExchangeMethods={allowedExchangeMethods}
        allowedPreferences={allowedPreferences}
        configurationPreferences={configurationPreferences}
        externalConnectedGateway={externalConnectedGateway}
        availableRecommendationTypes={availableRecommendationTypes}
      />
      <OrderIdentification
        allowedPreferences={allowedPreferences}
        allowsOrderRecovery={allowsOrderRecovery}
        hasOrderRecoveryEmailTemplate={hasOrderRecoveryEmailTemplate}
        onChangeAllowsOrderRecovery={setAllowsOrderRecovery}
      />
      <SkipRefundMethodStep
        allowedPreferences={allowedPreferences}
        skipSingleRefundMethodStep={skipSingleRefundMethodStep}
        onChangeSkipSingleRefundMethodStep={setSkipSingleRefundMethodStep}
      />
      <SaveChangesButtons
        hasChanges={hasPermission(userPermissions.editPolicy) && hasChanges}
        onSave={onSavePreferences}
        onRevertChanges={handleResetChanges}
        loading={savingPreferences}
        disabled={savingPreferences}
      />
    </Styles.Container>
  )
}
export default ReturnsPortalConfigurationPage
